import * as Accordion from '@radix-ui/react-accordion';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  Cross1Icon,
  GearIcon,
  LayersIcon,
  LightningBoltIcon,
  Link2Icon,
  LockClosedIcon,
  PersonIcon,
} from '@radix-ui/react-icons';
import { animated, useSpring } from '@react-spring/web';
import { Tooltip } from 'radix-ui';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAccount } from '~/apis';
import {
  DEBUG_enabled,
  isProductionEnvironment,
  selectIsSpectatorMode,
  selectLeftPanelOpen,
  useTranslation,
} from '~/app';
import { DEBUG_APP_URLS } from '~/app/routes';
import { useAuthState } from '~/auth';
import { useHasMounted } from '~/components/ClientOnly';
import { setDebugPanelState } from '~/overlay/actions';
import {
  BAR_BOTTOM_HEIGHT_PERCENT,
  BAR_TOP_HEIGHT_PERCENT,
  DEBUG_BUTTON_BOTTOM_OFFSET_REM,
  FAB_EDGE_INSET_REM,
  PANEL_BOTTOM_INSET_REM,
  PANEL_TOP_INSET_LEFT_REM,
  PANEL_TOP_INSET_LEFT_SPECTATOR_REM,
} from '~/overlay/overlaySpacing';
import {
  loadDebugMenuAccordionOpen,
  loadPanelExpanded,
  saveDebugMenuAccordionOpen,
  savePanelExpanded,
} from '~/overlay/panelPreferencesStorage';
import { UserRole } from '~/server/src/dtos';
import { cn } from '~/utils/styles';

import ActionEventTester from './ActionEventTester';
import AdminControls from './AdminControls';
import CommonSnapshotsPanel from './CommonSnapshotsPanel';
import { ReactComponent as DebugIcon } from './debug.svg';
import DebugControls from './DebugControls';
import TournamentControls from './TournamentControls';

const DebugMenu = (): JSX.Element | null => {
  const hasMounted = useHasMounted();
  const dispatch = useDispatch();
  const { data: account } = useAccount();
  const { isAuthorized } = useAuthState();
  const { t } = useTranslation();
  const leftPanelOpen = useSelector(selectLeftPanelOpen);
  const isSpectatorMode = useSelector(selectIsSpectatorMode);

  const [isExpanded, setIsExpanded] = useState(() => loadPanelExpanded('debugMenu') ?? false);
  const [isVisible, setIsVisible] = useState(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [openAccordionSections, setOpenAccordionSections] = useState<string[]>(
    () => loadDebugMenuAccordionOpen() ?? [],
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hasMountedPanel, setHasMountedPanel] = useState(false);

  // Replace direct window.innerWidth usage with safe default
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      const shouldCollapse = scrollTop > 20;
      setIsHeaderCollapsed(shouldCollapse);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isHeaderCollapsed]);

  const debugPanelSpring = useSpring({
    to: {
      width: isExpanded
        ? windowWidth >= 1024
          ? '58vw'
          : '65vw'
        : windowWidth >= 1024
          ? '34vw'
          : '40vw',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateX(0%)' : 'translateX(-100%)',
    },
    config: { tension: 280, friction: 26 },
  });

  const leftButtonsHidden = isVisible || leftPanelOpen;
  const debugIconSpring = useSpring({
    to: {
      opacity: leftButtonsHidden ? 0 : 1,
      transform: leftButtonsHidden ? 'translateX(-100%)' : 'translateX(0%)',
    },
    config: { tension: 300, friction: 28 },
  });

  useEffect(() => {
    dispatch(setDebugPanelState({ isVisible }));
  }, [dispatch, isVisible]);

  useEffect(() => {
    savePanelExpanded('debugMenu', isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    saveDebugMenuAccordionOpen(openAccordionSections);
  }, [openAccordionSections]);

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (event.defaultPrevented) return;
      const openOverlay = document.querySelector(
        '[role="dialog"][data-state="open"], [data-radix-select-content][data-state="open"]',
      );
      if (openOverlay) return;
      setIsVisible(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible]);

  const headerSpring = useSpring({
    to: {
      height: isHeaderCollapsed ? '36px' : '48px',
      paddingTop: isHeaderCollapsed ? '4px' : '8px',
      paddingBottom: isHeaderCollapsed ? '4px' : '8px',
    },
    config: { tension: 300, friction: 28 },
  });

  const toggleExpand = () => setIsExpanded(!isExpanded);
  const toggleVisibility = () => setIsVisible(!isVisible);

  // Check if debug menu should be visible. Not bound to frontend port (e.g. 8000); requires admin + non-production.
  // Defer until after mount so server and first client paint match (avoids hydration mismatch).
  const shouldShowDebugMenu =
    hasMounted &&
    DEBUG_enabled &&
    !!account &&
    isAuthorized &&
    account.role === UserRole.Admin &&
    !isProductionEnvironment;

  if (!shouldShowDebugMenu) {
    return null;
  }

  const panelTopInsetRem = isSpectatorMode
    ? PANEL_TOP_INSET_LEFT_SPECTATOR_REM
    : PANEL_TOP_INSET_LEFT_REM;

  const tooltipOffset = 6;
  const showFab = !isVisible;
  // Keep the panel mounted once it has first become visible (adjust state during render).
  if (isVisible && !hasMountedPanel) {
    setHasMountedPanel(true);
  }
  const showPanel = hasMountedPanel || isVisible;

  return (
    <>
      {showFab && (
        <Tooltip.Provider delayDuration={0}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <animated.button
                onClick={toggleVisibility}
                style={{
                  ...debugIconSpring,
                  bottom: `calc(${BAR_BOTTOM_HEIGHT_PERCENT}% + ${DEBUG_BUTTON_BOTTOM_OFFSET_REM}rem)`,
                  left: `${FAB_EDGE_INSET_REM}rem`,
                  pointerEvents: leftPanelOpen ? 'none' : 'auto',
                }}
                className={cn(
                  'fixed z-50 flex h-9 w-9 items-center justify-center rounded-full bg-[#405820] text-white shadow-lg md:h-10 md:w-10',
                )}
                aria-label={t('button_debug')}
              >
                <DebugIcon
                  className="h-4 w-4 md:h-5 md:w-5 [&_*]:stroke-white"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </animated.button>
            </Tooltip.Trigger>
            <Tooltip.Content
              side="right"
              sideOffset={tooltipOffset}
              className="rounded-lg border bg-white px-2 py-1 text-sm shadow-lg"
            >
              {t('button_debug')}
            </Tooltip.Content>
          </Tooltip.Root>
        </Tooltip.Provider>
      )}

      {showPanel && (
      <animated.div
        style={{
          ...debugPanelSpring,
          top: `calc(${BAR_TOP_HEIGHT_PERCENT}% + ${panelTopInsetRem}rem)`,
          bottom: `calc(${BAR_BOTTOM_HEIGHT_PERCENT}% + ${PANEL_BOTTOM_INSET_REM}rem)`,
        }}
        className={cn(
          '@container absolute left-0 z-40 rounded-tr-lg rounded-br-lg border-2 border-[#405820]/40 bg-white/90 select-text',
          isVisible ? 'pointer-events-auto' : 'pointer-events-none',
        )}
        aria-hidden={!isVisible}
        inert={!isVisible ? true : undefined}
      >
        {/* Control buttons positioned outside scrollable area */}
        <div className="absolute top-1/2 right-0 z-50 translate-x-[50%] -translate-y-1/2 transform">
          <button
            onClick={toggleExpand}
            className="mb-2 block rounded-r-md bg-[#405820] p-2 text-white"
          >
            {isExpanded ? (
              <ChevronLeftIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={toggleVisibility}
            className="block rounded-r-md bg-[#405820] p-2 text-white"
          >
            <Cross1Icon className="h-4 w-4" />
          </button>
        </div>
        {/* Scrollable content area */}
        <div ref={scrollContainerRef} className="h-full overflow-auto">
          <div className="selectable-text flex flex-col space-y-2">
            <animated.div
              style={headerSpring}
              className="sticky top-0 z-10 flex items-center rounded-tr-lg border-b border-[#405820]/20 bg-white/90 pr-6 pl-4 backdrop-blur-sm"
            >
              <h2 className="flex min-w-0 items-center gap-2 text-lg font-bold text-[#405820]">
                <GearIcon className="h-5 w-5 flex-shrink-0" />
                <span className="min-w-0 truncate">
                  {t('debug_header_title') || 'Turfmaster Debugger'}
                </span>
              </h2>
            </animated.div>

            <div className="space-y-4 pr-6 pl-2">
              <Accordion.Root
                type="multiple"
                value={openAccordionSections}
                onValueChange={setOpenAccordionSections}
                className="space-y-2"
              >
                <Accordion.Item value="app-urls" className="rounded-lg border border-[#405820]/20">
                  <Accordion.Header className="flex items-center p-3">
                    <Accordion.Trigger className="group -m-1 flex w-full items-center justify-between rounded p-1 text-left transition-colors hover:bg-[#405820]/5">
                      <Link2Icon className="mr-2 h-5 w-5 flex-shrink-0 text-[#405820]" />
                      <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-[#405820]">
                        App URLs
                      </h3>
                      <ChevronUpIcon className="ml-2 h-4 w-4 flex-shrink-0 text-[#405820] transition-transform duration-200 group-data-[state=closed]:rotate-180" />
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className="px-3 pb-3">
                    <ul className="max-h-[min(40vh,16rem)] space-y-1.5 overflow-y-auto text-sm">
                      {DEBUG_APP_URLS.map(({ path, label }) => (
                        <li key={path}>
                          <a
                            href={path}
                            className="break-all text-[#405820] hover:underline"
                            target={path.startsWith('http') ? '_blank' : undefined}
                            rel={path.startsWith('http') ? 'noopener noreferrer' : undefined}
                          >
                            {label}
                          </a>
                          {!path.startsWith('http') && (
                            <span className="ml-1 font-mono text-xs text-[#405820]/60">{path}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item
                  value="debug-controls"
                  className="rounded-lg border border-[#405820]/20"
                >
                  <Accordion.Header className="flex items-center p-3">
                    <Accordion.Trigger className="group -m-1 flex w-full items-center justify-between rounded p-1 text-left transition-colors hover:bg-[#405820]/5">
                      <PersonIcon className="mr-2 h-5 w-5 flex-shrink-0 text-[#405820]" />
                      <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-[#405820]">
                        Account Management
                      </h3>
                      <ChevronUpIcon className="ml-2 h-4 w-4 flex-shrink-0 text-[#405820] transition-transform duration-200 group-data-[state=closed]:rotate-180" />
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className="px-3 pb-3">
                    <DebugControls />
                  </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item
                  value="common-snapshots"
                  className="rounded-lg border border-[#405820]/20"
                >
                  <Accordion.Header className="flex items-center p-3">
                    <Accordion.Trigger className="group -m-1 flex w-full items-center justify-between rounded p-1 text-left transition-colors hover:bg-[#405820]/5">
                      <LayersIcon className="mr-2 h-5 w-5 flex-shrink-0 text-[#405820]" />
                      <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-[#405820]">
                        {t('snapshots_section_common')}
                      </h3>
                      <ChevronUpIcon className="ml-2 h-4 w-4 flex-shrink-0 text-[#405820] transition-transform duration-200 group-data-[state=closed]:rotate-180" />
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className="px-3 pb-3">
                    <CommonSnapshotsPanel onSnapshotStart={() => setIsVisible(false)} />
                  </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item
                  value="action-events"
                  className="rounded-lg border border-[#405820]/20"
                >
                  <Accordion.Header className="flex items-center p-3">
                    <Accordion.Trigger className="group -m-1 flex w-full items-center justify-between rounded p-1 text-left transition-colors hover:bg-[#405820]/5">
                      <LightningBoltIcon className="mr-2 h-5 w-5 flex-shrink-0 text-[#405820]" />
                      <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-[#405820]">
                        Action Events
                      </h3>
                      <ChevronUpIcon className="ml-2 h-4 w-4 flex-shrink-0 text-[#405820] transition-transform duration-200 group-data-[state=closed]:rotate-180" />
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className="px-3 pb-3">
                    <ActionEventTester onClose={() => setIsVisible(false)} />
                  </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item
                  value="tournament-tools"
                  className="rounded-lg border border-[#405820]/20"
                >
                  <Accordion.Header className="flex items-center p-3">
                    <Accordion.Trigger className="group -m-1 flex w-full items-center justify-between rounded p-1 text-left transition-colors hover:bg-[#405820]/5">
                      <CalendarIcon className="mr-2 h-5 w-5 flex-shrink-0 text-[#405820]" />
                      <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-[#405820]">
                        Turniere
                      </h3>
                      <ChevronUpIcon className="ml-2 h-4 w-4 flex-shrink-0 text-[#405820] transition-transform duration-200 group-data-[state=closed]:rotate-180" />
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className="px-3 pb-3">
                    <TournamentControls className="w-full" />
                  </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item
                  value="admin-tools"
                  className="rounded-lg border border-[#405820]/20"
                >
                  <Accordion.Header className="flex items-center p-3">
                    <Accordion.Trigger className="group -m-1 flex w-full items-center justify-between rounded p-1 text-left transition-colors hover:bg-[#405820]/5">
                      <LockClosedIcon className="mr-2 h-5 w-5 flex-shrink-0 text-[#405820]" />
                      <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-[#405820]">
                        Admin Tools
                      </h3>
                      <ChevronUpIcon className="ml-2 h-4 w-4 flex-shrink-0 text-[#405820] transition-transform duration-200 group-data-[state=closed]:rotate-180" />
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className="px-3 pb-3">
                    <AdminControls className="w-full" />
                  </Accordion.Content>
                </Accordion.Item>
              </Accordion.Root>
            </div>
            <div className="pb-2"></div>
          </div>
        </div>
      </animated.div>
      )}
    </>
  );
};

export default DebugMenu;
